import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { HiddenAnchor, FixedTooltipWrapper } from './TooltipStyles';

const ARROW_OFFSET = 8;
const HIDE_DELAY_MS = 150;

const computePosition = (rect, placement) => {
	if (placement === 'below') {
		return {
			top: rect.bottom + ARROW_OFFSET,
			left: rect.left + rect.width / 2,
			transform: 'translateX(-50%)'
		};
	}
	if (placement === 'right') {
		return {
			top: rect.top + rect.height / 2,
			left: rect.right + ARROW_OFFSET,
			transform: 'translateY(-50%)'
		};
	}
	if (placement === 'left') {
		return {
			top: rect.top + rect.height / 2,
			left: rect.left - ARROW_OFFSET,
			transform: 'translate(-100%, -50%)'
		};
	}
	return {
		top: rect.top - ARROW_OFFSET,
		left: rect.left + rect.width / 2,
		transform: 'translate(-50%, -100%)'
	};
};

const resolvePlacement = ({ top_arr, left_arr, right_arr }) => {
	if (top_arr) return 'below';
	if (left_arr) return 'right';
	if (right_arr) return 'left';
	return 'above';
};

const PortaledTooltip = ({ children, ...rest }) => {
	const anchorRef = useRef(null);
	const hideTimerRef = useRef(null);
	const [pos, setPos] = useState(null);
	const placement = resolvePlacement(rest);

	const clearHideTimer = useCallback(() => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
			hideTimerRef.current = null;
		}
	}, []);

	const scheduleHide = useCallback(() => {
		clearHideTimer();
		hideTimerRef.current = setTimeout(() => setPos(null), HIDE_DELAY_MS);
	}, [clearHideTimer]);

	useEffect(() => {
		const parent = anchorRef.current?.parentElement;
		if (!parent) return undefined;

		const show = () => {
			clearHideTimer();
			setPos(computePosition(parent.getBoundingClientRect(), placement));
		};
		const hideImmediate = () => {
			clearHideTimer();
			setPos(null);
		};

		parent.addEventListener('mouseenter', show);
		parent.addEventListener('mouseleave', scheduleHide);
		window.addEventListener('scroll', hideImmediate, true);
		window.addEventListener('resize', hideImmediate);

		return () => {
			clearHideTimer();
			parent.removeEventListener('mouseenter', show);
			parent.removeEventListener('mouseleave', scheduleHide);
			window.removeEventListener('scroll', hideImmediate, true);
			window.removeEventListener('resize', hideImmediate);
		};
	}, [placement, scheduleHide, clearHideTimer]);

	return (
		<>
			<HiddenAnchor ref={anchorRef} aria-hidden='true' />
			{pos && createPortal(
				<FixedTooltipWrapper
					className='tooltip-wrap'
					{...rest}
					posTop={`${pos.top}px`}
					posLeft={`${pos.left}px`}
					transform={pos.transform}
					display='block'
					onMouseEnter={clearHideTimer}
					onMouseLeave={scheduleHide}>
					{children}
				</FixedTooltipWrapper>,
				document.body
			)}
		</>
	);
};

export default PortaledTooltip;
